// pineCones.js — pine cones hung on conifer branches (pine + candle only).
//
// A GLB accent (assets/models/pine-cone-c.glb) cloned a few times onto eligible
// branches near their tips. The clones are parented into each Branch's foliageGroup
// (see Branch.createPineCones in tree.js), so they track shaping, drop on prune, and
// are cleared/rebuilt with the foliage automatically.
//
// There is no toggle: cones appear automatically on the needle types whenever the
// foliage ISN'T the fresh Spring-Green flush (parameters.foliageColor === 'spring'),
// the one season too young to have set cones. On the plain Green palette they're a
// fresh summer GREEN (young cones); every other colour gets the mature brown GLB look.
// All of this is derived from leafType + foliageColor (both already saved), so cones
// reproduce across saves with no state of their own. Placement is cosmetic scatter on
// Math.random (never the seeded tree stream) — intentionally not reproduced position-
// for-position. The GLB loads once at boot; if it lands after the tree is built, the
// onLoad refreshes the live tree to catch up.

import * as THREE from 'three';
import { GLTFLoader } from 'three/addons/loaders/GLTFLoader.js';
import { app, parameters } from './core.js';

const MODEL_FILE = 'assets/models/pine-cone-c.glb';

// Target longest-dimension of one cone in world units (the GLB is normalized to it,
// then re-centred on the origin so callers can position by the cone's middle).
export const CONE_LENGTH = 9;

let template = null;   // cached, pre-scaled+centred cone; clone() per placement
let matMature = null;  // the GLB's own material (brown, textured — a ripe cone)
let matYoung = null;   // flat fresh-green variant (untextured) — a young summer cone
let loading = false;

// Pine cones belong only to true pines (short + long-needle "candle"), and only once
// past the fresh Spring-Green flush (too young to have set cones).
export function pineConesWanted() {
  return (parameters.leafType === 'pine' || parameters.leafType === 'candle') &&
    parameters.foliageColor !== 'spring';
}

// Kick off the one-time GLB load. When it lands, refresh the live tree so the cones
// appear without a regenerate (e.g. the GLB finished after the first tree was built).
export function initPineCones() {
  if (template || loading) return;
  loading = true;
  new GLTFLoader().load(MODEL_FILE, (gltf) => {
    const root = gltf.scene;

    // Normalize to CONE_LENGTH along its longest axis...
    const size = new THREE.Vector3();
    new THREE.Box3().setFromObject(root).getSize(size);
    root.scale.setScalar(CONE_LENGTH / Math.max(size.x, size.y, size.z, 1e-3));
    root.updateMatrixWorld(true);

    // ...then re-centre on the origin so positions reference the cone's middle.
    const centre = new THREE.Vector3();
    new THREE.Box3().setFromObject(root).getCenter(centre);
    root.position.sub(centre);

    // The GLB ships one brown, texture-mapped material (the ripe cone). Keep it as the
    // mature material; derive a young one by dropping the brown texture for a flat
    // fresh green (tinting green over the brown map would just go muddy).
    root.traverse(o => { if (o.isMesh && o.material && !matMature) matMature = o.material; });
    if (matMature) {
      matYoung = matMature.clone();
      matYoung.map = null;
      matYoung.color = new THREE.Color(0x7c9a44); // fresh summer green
    }

    template = root;
    loading = false;
    if (app.tree && app.tree.refreshPineCones) app.tree.refreshPineCones();
  }, undefined, () => { loading = false; });
}

// One ready-to-place cone clone, or null if the GLB hasn't loaded yet. Geometry and
// materials are shared (Object3D.clone keeps refs; both materials are reused across
// all cones). Shadows on; raycast disabled so cones never intercept the prune/shape
// picker. On the Green palette the cone wears the fresh-green "young" material.
export function makeCone() {
  if (!template) return null;
  const cone = template.clone(true);
  cone.userData.isPineCone = true;
  const young = parameters.foliageColor === 'greens' && matYoung;
  cone.traverse(o => {
    if (o.isMesh) {
      o.castShadow = true; o.receiveShadow = true; o.raycast = () => {};
      if (young) o.material = matYoung;
    }
  });
  return cone;
}
